import { create } from "zustand";
import { cn } from "../../lib/cn";
import { IconCheck, IconDanger, IconX } from "../icons";

interface Toast {
  id: number;
  message: string;
  tone: "default" | "error" | "success";
  action?: { label: string; onClick: () => void };
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, "id" | "duration" | "tone"> & { tone?: Toast["tone"]; duration?: number }) => number;
  dismiss: (id: number) => void;
}

let seq = 0;
const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  push: (t) => {
    const id = ++seq;
    const toast: Toast = { tone: "default", ...t, duration: t.duration ?? (t.action ? 6000 : 3500), id };
    set({ toasts: [...get().toasts.slice(-2), toast] });
    setTimeout(() => get().dismiss(id), toast.duration);
    return id;
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

/** Toasts are reserved for confirmations outside the immediate interaction and failures. */
export const toast = {
  show: (message: string, action?: Toast["action"], duration?: number) => useToasts.getState().push({ message, action, duration }),
  error: (message: string) => useToasts.getState().push({ message, tone: "error" }),
  success: (message: string) => useToasts.getState().push({ message, tone: "success" }),
};

export function ToastHost() {
  const { toasts, dismiss } = useToasts();
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-20 z-[90] flex flex-col items-center gap-2 px-4 md:bottom-6">
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.tone === "error" ? "alert" : "status"}
          className="pointer-events-auto flex max-w-md items-center gap-3 rounded-lg bg-surface-2 py-2 pl-3 pr-2 text-sm text-fg shadow-pop animate-slide-up"
        >
          {t.tone === "error" && <IconDanger className="icon size-[18px] text-danger" aria-hidden />}
          {t.tone === "success" && <IconCheck className="icon size-[18px] text-success" aria-hidden />}
          <span className="min-w-0 flex-1">{t.message}</span>
          {t.action && (
            <button
              onClick={() => {
                t.action!.onClick();
                dismiss(t.id);
              }}
              className="rounded-md px-2 py-1 text-sm font-semibold text-fg hover:bg-surface-3"
            >
              {t.action.label}
            </button>
          )}
          <button aria-label="Dismiss" onClick={() => dismiss(t.id)} className={cn("rounded-md p-1 text-fg-3 hover:bg-surface-3 hover:text-fg")}>
            <IconX className="icon size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
